import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  acknowledgePlatformFeeRate,
  formatRateEffectiveDate,
  formatRatePercent,
} from "../utils/platformFee";
import getApiBase from "./getApiBase";

const PlatformFeeRateChangeModal = ({
  open,
  onClose,
  notice,
  shopId,
  isOwner = true,
  onAcknowledged,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!notice?.required) return null;

  const handleAcknowledge = async () => {
    if (!isOwner) {
      onClose?.();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await acknowledgePlatformFeeRate(getApiBase(), shopId, notice.versionId);
      onAcknowledged?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Could not save acknowledgment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <InfoOutlinedIcon color="primary" />
        Platform fee rate update
      </DialogTitle>
      <DialogContent>
        {isOwner ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The platform fee for your shop changed from{" "}
              <strong>{formatRatePercent(notice.previousRate)}</strong> to{" "}
              <strong>{formatRatePercent(notice.newRate)}</strong>
              {notice.effectiveAt ? (
                <>
                  , effective{" "}
                  <strong>{formatRateEffectiveDate(notice.effectiveAt)}</strong>
                </>
              ) : null}
              .
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Platform fees are accrued when jobs complete — not when you submit a
              quote. Please acknowledge before your next quote.
            </Typography>
          </>
        ) : (
          <Typography variant="body1">
            The shop&apos;s platform fee rate changed. Ask the shop owner to review
            and acknowledge the update before submitting quotes.
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {isOwner ? "Not now" : "Close"}
        </Button>
        {isOwner && (
          <Button
            variant="contained"
            onClick={handleAcknowledge}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? "Saving…" : "I understand — continue"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlatformFeeRateChangeModal;
